import dynamic from 'next/dynamic';
import Head from 'next/head';
import Layout from '@/components/Layout';
import MetaSeo from '@/components/MetaSeo';

const CaseStudies = dynamic(() => import('@/components/CaseStudies'));

const SITE_URL = 'https://eyefire.vn';
const SUPPORTED_LOCALES = ['vi', 'en', 'jp'];
const DEFAULT_LOCALE = 'vi';

function buildLocaleUrl(locale, path) {
  const suffix = path || '';
  return locale === DEFAULT_LOCALE
    ? `${SITE_URL}${suffix}`
    : `${SITE_URL}/${locale}${suffix}`;
}

const SEO_COPY = {
  vi: {
    title: 'Case Studies | EYEFIRE',
    description:
      'Khám phá các case studies AI an toàn của EyeFire trong sản xuất, logistics, xây dựng và bán lẻ — cách doanh nghiệp giảm rủi ro, phòng ngừa sự cố và nâng cao trực quan vận hành.',
    keywords:
      'EyeFire case studies, AI an toàn, giám sát thông minh, computer vision, nhận diện khuôn mặt, an toàn lao động, công nghệ AI Việt Nam',
  },
  en: {
    title: 'Case Studies | EYEFIRE',
    description:
      'Explore EyeFire safety-AI case studies across manufacturing, logistics, construction and retail — how teams reduce risk, prevent incidents and gain operational visibility.',
    keywords:
      'EyeFire case studies, safety AI, computer vision, intelligent monitoring, face recognition, workplace safety, AI Vietnam',
  },
  jp: {
    title: 'Case Studies | EYEFIRE',
    description:
      '製造・物流・建設・小売における EyeFire の安全AI事例集。リスク低減・事故防止・運用の可視化を実現する活用方法をご紹介します。',
    keywords:
      'EyeFire 事例, 安全AI, コンピュータビジョン, スマート監視, 顔認証, 労働安全, AI ベトナム',
  },
};

function Page(props) {
  const locale = props.sign || DEFAULT_LOCALE;
  const copy = SEO_COPY[locale] || SEO_COPY[DEFAULT_LOCALE];
  const canonical = buildLocaleUrl(locale, '/case-studies');

  return (
    <>
      <MetaSeo
        url={canonical}
        type='website'
        title={copy.title}
        description={copy.description}
        keywords={copy.keywords}
        image={`${SITE_URL}/assets/images/eyefire_logo.png`}
      />
      <Head>
        <link rel='canonical' href={canonical} key='canonical' />
        {SUPPORTED_LOCALES.map((loc) => (
          <link
            key={loc}
            rel='alternate'
            hrefLang={loc === 'jp' ? 'ja' : loc}
            href={buildLocaleUrl(loc, '/case-studies')}
          />
        ))}
        <link
          rel='alternate'
          hrefLang='x-default'
          href={buildLocaleUrl(DEFAULT_LOCALE, '/case-studies')}
        />
      </Head>
      <CaseStudies cases={props.cases || []} />
    </>
  );
}

Page.getLayout = function getLayout(page) {
  return (
    <Layout web='case-studies' webViewMobile={true}>
      {page}
    </Layout>
  );
};

export async function getServerSideProps(context) {
  try {
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_API_SERVER_URL}/app/case_study/list?limit=100`,
      {
        headers: {
          'Content-Type': 'application/json',
          locale: context.locale || 'vi',
        },
      }
    );
    const data = await res.json();
    if (!data || data.code !== 200) {
      return { props: { cases: [], sign: context.locale || 'vi' } };
    }
    const rows = (data.data && data.data.rows) || [];
    return { props: { cases: rows, sign: context.locale || 'vi' } };
  } catch (error) {
    console.log('case-studies list fetch failed', error && error.message);
    return { props: { cases: [], sign: context.locale || 'vi' } };
  }
}

export default Page;
